'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
}

const HomeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const LibraryIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);
const MissionsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <circle cx="12" cy="12" r="6"/>
    <circle cx="12" cy="12" r="2"/>
  </svg>
);
const BountyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
    <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
);
const PassportIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="18" rx="2" ry="2"/>
    <circle cx="12" cy="10" r="3"/>
    <path d="M6 21v-1a6 6 0 0 1 12 0v1"/>
  </svg>
);
const EventsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const RewardsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 12 20 22 4 22 4 12"/>
    <rect x="2" y="7" width="20" height="5"/>
    <line x1="12" y1="22" x2="12" y2="7"/>
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
  </svg>
);
const AdminIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const StarIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const publicNavItems: NavItem[] = [
  { label: 'Home', href: '/home', icon: <HomeIcon /> },
  { label: 'Library', href: '/library', icon: <LibraryIcon /> },
  { label: 'Missions', href: '/missions', icon: <MissionsIcon /> },
  { label: 'Bounty Board', href: '/bounty-board', icon: <BountyIcon /> },
  { label: 'Passport', href: '/passport', icon: <PassportIcon /> },
  { label: 'Events', href: '/events', icon: <EventsIcon /> },
  { label: 'Rewards', href: '/rewards', icon: <RewardsIcon /> },
];

const adminNavItem: NavItem = { label: 'Admin', href: '/admin', icon: <AdminIcon />, adminOnly: true };

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function Sidebar({ collapsed = false, onToggle, mobileOpen = false, onMobileClose }: SidebarProps) {
  const pathname = usePathname();
  const { profile, canAccessAdmin } = useAuth();

  const navItems = canAccessAdmin()
    ? [...publicNavItems, adminNavItem]
    : publicNavItems;

  const sidebarClasses = [
    'gxg-sidebar',
    collapsed ? 'collapsed' : '',
    mobileOpen ? 'mobile-open' : '',
  ].filter(Boolean).join(' ');

  return (
    <aside className={sidebarClasses}>
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-white/5">
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 via-blue-500 to-pink-500 flex items-center justify-center">
          <span className="font-display font-black text-white text-xs">G</span>
        </div>
        {!collapsed && (
          <div>
            <div className="font-display font-black text-lg gxg-gradient-text leading-none">GXG</div>
            <div className="text-[9px] font-mono text-white/30 tracking-widest uppercase mt-0.5">Retro Passport</div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
        {navItems.map((item) => {
          const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
          return (
            <Link
              key={item.href}
              href={item.href}
              className={`gxg-nav-item ${isActive ? 'active' : ''}`}
              title={collapsed ? item.label : undefined}
              onClick={onMobileClose}
            >
              <span className="flex-shrink-0">{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Player Level Card */}
      {!collapsed && profile && (
        <div className="player-level-card mx-2 mb-2">
          <div className="text-[9px] font-mono text-white/40 tracking-widest uppercase mb-1">GXG Member</div>
          <div className="font-display font-bold text-base gxg-blue-text mb-2">LEVEL {profile.level}</div>
          <div className="xp-bar mb-1">
            <div className="xp-bar-fill" style={{ width: `${Math.min(100, (profile.xp % 10000) / 100)}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-white/40 font-mono mb-3">
            <span>{profile.xp.toLocaleString()} XP</span>
            <span>{((Math.floor(profile.xp / 10000) + 1) * 10000).toLocaleString()} XP</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
              {(profile.full_name || profile.email || 'G').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="text-xs font-semibold text-white/80">{profile.full_name || profile.username || profile.email?.split('@')[0]}</div>
              <div className="text-[10px] text-white/40 capitalize">{profile.tier}</div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-white/5">
            <div className="text-[9px] text-white/30 font-mono uppercase tracking-wider">GXG Coins</div>
            <div className="text-[11px] text-gxg-gold font-semibold mt-0.5 flex items-center gap-1">
              <StarIcon />
              {profile.gxg_coins.toLocaleString()} coins
            </div>
          </div>
        </div>
      )}

      {/* Collapse Toggle */}
      <button
        onClick={onToggle}
        className="flex items-center justify-center w-full h-10 border-t border-white/5 text-white/30 hover:text-white/60 transition-colors"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </button>
    </aside>
  );
}
